import { type FC } from "react";

import type { ICommonServiceOutput } from "@/Components/Api/CommonServiceOutput";
import Button from "@/design-system/button";
import { languageSelector } from "@/src/contexts";
import { VoucherResources } from "@/src/voucher/Voucher.Resources";
import { VoucherResourcesEn } from "@/src/voucher/Voucher.Resources.En";
import { getAssetsImage } from "@Components/Modules/Helpers";

import { ExpandableList } from "../common/ExpandableList";
import type { IExpandableListItem } from "../common/ExpandableListItem";
import { Success } from "../common/icons/Success";

import * as portalCss from "../shared/Portal.module.css";
import * as css from "./Voucher.module.css";

export enum VoucherCampaignType {
    Undefined,
    GiftCards,
    Cashback,
    MGM,
    ReloadableCards,
    CashbackSpecialOffer,
}

enum UseVoucherActionType {
    Close,
    GoToInternalUrl,
}

export type IVoucher = {
    faqItems: IExpandableListItem[];
    internalUrl?: string;
    expirationDate?: string;
    voucherCampaignType: VoucherCampaignType;
    debitCardValue?: string;
    commonServiceOutput?: ICommonServiceOutput<UseVoucherActionType>;
};

const Voucher: FC<IVoucher> = ({
    voucherCampaignType,
    faqItems,
    internalUrl,
    expirationDate,
    debitCardValue,
    commonServiceOutput,
}) => {
    const resources = languageSelector(VoucherResources, VoucherResourcesEn);

    const isCbBonus = voucherCampaignType === VoucherCampaignType.Cashback;
    const isCbSpecialOfferBonus = voucherCampaignType === VoucherCampaignType.CashbackSpecialOffer;

    const title = isCbBonus
        ? resources.cshbTitle
        : isCbSpecialOfferBonus
          ? commonServiceOutput.userCommunication.title
          : resources.ggcTitle;

    const description = isCbBonus
        ? resources.cshbDescription
        : isCbSpecialOfferBonus
          ? commonServiceOutput.userCommunication.message
          : resources.ggcDescription;

    const newUrl = internalUrl ? new URL(internalUrl).pathname : "/";
    return (
        <div className={`${css.voucherComponent} ${portalCss.standardContainerV2}`}>
            <div className={css.wrapper}>
                {commonServiceOutput?.resultStatus?.success === false || voucherCampaignType === 0 ? (
                    <>
                        <img src={getAssetsImage(process.env.ErrorIcon)} alt="error" />
                        <p className={css.title}>{commonServiceOutput.userCommunication.title}</p>
                        <p className={css.description}>{commonServiceOutput.userCommunication.message}</p>
                    </>
                ) : (
                    <>
                        <Success iconAppearance="Green" />
                        <p className={css.title}>{title.replace("{amount}", debitCardValue)}</p>
                        <p className={css.description}>{description}</p>
                        {expirationDate && (
                            <p className={css.subtitle}>
                                {resources.activationTimeDescription.replace("{date}", expirationDate)}
                            </p>
                        )}

                        {(isCbBonus || isCbSpecialOfferBonus) && internalUrl && (
                            <Button as="a" size="M" variant="magentaRound" href={newUrl}>
                                {resources.cbButtonText}
                            </Button>
                        )}

                        {faqItems && (
                            <div className={css.faqWrapper}>
                                <h3 className={css.faqTitle}>{resources.faqTitle}</h3>
                                <ExpandableList initialData={faqItems} preventOnItemClick={false} />
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export { Voucher };
